import type { ReactNode } from "react";
import type { PageContext } from "vike/types";
import { usePageContext } from "vike-react/usePageContext";

import { getHeadTranslation, getHeadTranslations } from "@/3-features/i18n/headTranslations";
import { OGMetaTags, TwitterCard } from "@/3-features/seo/seoHelpers";

export const ORIGIN = "https://dec-energy.ch";

export type HeadKey = Parameters<typeof getHeadTranslations>[1];

/**
 * Standard leaf-route `<head>`: description + Open Graph + Twitter, resolved
 * from `heads.json`. Collapses the ~30-line block every route repeated.
 * - `suffix`, locale-relative path (`""` = home, `"company/product"`, …).
 * - `guard`, emit only on exactly `/${suffix}`; for landing +Head files that
 *   Vike otherwise cascades onto child routes.
 * - `extra(locale)`, page-specific tags appended after the standard set
 *   (e.g. the FAQ JSON-LD).
 */
export function makeHead(
  key: HeadKey,
  suffix: string,
  opts: { guard?: boolean; extra?: (locale: "it" | "en") => ReactNode } = {},
) {
  return function Head() {
    const pageContext = usePageContext();
    const locale = pageContext.locale ?? "it";
    if (opts.guard) {
      const path = (pageContext.urlLogical ?? pageContext.urlPathname ?? "").replace(/\/$/, "");
      if (path !== `/${suffix}`) return null;
    }
    const { description, ogTitle, ogDescription } = getHeadTranslations(locale, key);
    const url = suffix ? `${ORIGIN}/${locale}/${suffix}` : `${ORIGIN}/${locale}`;

    return (
      <>
        <meta name="description" content={description} />
        <OGMetaTags description={ogDescription} url={url} locale={locale} />
        <TwitterCard title={ogTitle} description={ogDescription} />
        {opts.extra?.(locale === "en" ? "en" : "it")}
      </>
    );
  };
}

/** Standard leaf-route document title from `heads.json`. */
export function makeTitle(key: HeadKey) {
  return (pageContext: PageContext) =>
    getHeadTranslation(pageContext.locale ?? "it", key, "pageTitle");
}

/** robots noindex, embed / public-profile / 404-style routes kept out of the index. */
export function NoIndexHead() {
  return <meta name="robots" content="noindex, follow" />;
}
